import React from 'react';
import { object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { selectListingCollection } from '../../ducks/listingCollections.duck';
import {
  MIN_VENDORS,
  categoriesWithPage,
  hasCategoryRegionPage,
  regionalFirst,
  regionalVendorCount,
} from '../../util/categoryRegions';
import {
  cantonBySlug,
  neighboursOf,
  regionSearchMode,
  regionSearchString,
  regionSlug,
} from '../../util/regions';
import { categoryRegionPageSchema } from '../../util/landingSchema';

import { NamedLink } from '../../components';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import LandingLayout from './LandingLayout';
import { CATEGORIES, STEPS, SHARED_REGION_FAQ } from './landingContent';
import categoriesData from './data/categories.json';
import {
  categoryRegionCollectionId,
  categoryRegionFilters,
} from './CategoryRegionLandingPage.duck';

import Hero from './sections/Hero';
import ClaimBanner from './sections/ClaimBanner';
import Usps from './sections/Usps';
import ListingGrid from './sections/ListingGrid';
import Explainer from './sections/Explainer';
import InfoCards from './sections/InfoCards';
import CardGrid from './sections/CardGrid';
import Steps from './sections/Steps';
import Faq from './sections/Faq';
import TrustBanner from './sections/TrustBanner';
import Testimonials from './sections/Testimonials';
import FinalCta from './sections/FinalCta';

const pageLink = (slug, option) => ({
  name: 'CategoryRegionLandingPage',
  params: { slug, region: regionSlug(option) },
});

export const CategoryRegionLandingPageComponent = props => {
  const { params, listings, scrollingDisabled } = props;
  const config = useConfiguration();
  const category = CATEGORIES.find(c => c.slug === params?.slug);
  const copy = categoriesData.categories?.[params?.slug];
  const canton = cantonBySlug(params?.region);

  if (!category || !copy || !canton || !hasCategoryRegionPage(category.slug, canton.option)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const inCanton = `im Kanton ${canton.label}`;
  const heading = `${copy.region.heading} ${inCanton}`;
  const faq = (copy.faq?.items || []).concat(SHARED_REGION_FAQ);
  const cards = regionalFirst(listings);

  // Without a searchable Region field the grid stays empty, so the CTA drops
  // the canton rather than promise a filter the search page would ignore.
  const categorySearch = copy.searchHref.replace(/^[^?]*/, '');
  const search = categoryRegionFilters(params, config)
    ? `${categorySearch}&${regionSearchString(canton.option, regionSearchMode(config)).slice(1)}`
    : categorySearch;
  const searchLink = { name: 'SearchPage', to: { search } };
  const categoryLink = { name: 'CategoryLandingPage', params: { slug: category.slug } };

  return (
    <LandingLayout
      title={`${copy.region.title} ${canton.label} – Angebote vergleichen & buchen | Toastably`}
      description={`${heading} vergleichen: Angebote von Anbieter:innen, die ${inCanton} arbeiten. Transparente Preise, direkt online buchbar.`}
      schema={categoryRegionPageSchema(
        { ...copy, slug: category.slug, shortLabel: category.label },
        canton,
        faq
      )}
      currentPage="CategoryRegionLandingPage"
      scrollingDisabled={scrollingDisabled}
      noindex={regionalVendorCount(cards) < MIN_VENDORS}
    >
      <Hero
        crumbs={[
          { label: 'Startseite', link: { name: 'LandingPage' } },
          { label: 'Kategorien', link: { name: 'CategoryHubPage' } },
          { label: category.label, link: categoryLink },
          { label: canton.label },
        ]}
        heading={heading}
        lead={`Hier findet ihr Anbieter:innen aus der Kategorie ${category.label}, die ${inCanton} arbeiten. Vergleicht ihre Angebote und bucht direkt über Toastably.`}
        primaryCta={{ link: searchLink, label: 'Jetzt Angebote entdecken' }}
        secondaryCta={{ link: { href: '#faq' }, label: 'Häufige Fragen' }}
      />

      <ClaimBanner />
      <Usps />

      <ListingGrid
        listings={cards}
        heading={`Anbieter:innen ${inCanton}`}
        text="Wer nur in einzelnen Kantonen arbeitet, steht zuerst."
        emptyState={
          <>
            <p className="listings-empty">
              {`Für ${category.label} ${inCanton} sind aktuell keine Angebote ausgeschrieben.`}
            </p>
            <div style={{ textAlign: 'center', marginTop: '40px' }}>
              <NamedLink
                className="btn btn-ghost"
                name="CategoryLandingPage"
                params={categoryLink.params}
              >
                {`${category.label} in der ganzen Schweiz`}
              </NamedLink>
            </div>
          </>
        }
        footerLink={{ link: searchLink, label: 'Alle Angebote ansehen' }}
      />

      <Explainer
        heading={copy.explainer?.heading}
        text={copy.explainer?.text}
        items={copy.explainer?.checklist}
      />

      <Steps heading={STEPS.heading} text={STEPS.text} items={copy.steps?.items || STEPS.items} />

      <InfoCards heading={copy.info?.heading} text={copy.info?.text} cards={copy.info?.cards} />

      <Faq heading={copy.faq?.heading || 'Häufige Fragen'} items={faq} />

      <CardGrid
        id="neighbours-title"
        heading={`${category.label} in angrenzenden Kantonen`}
        text="Viele Anbieter:innen arbeiten kantonsübergreifend."
        items={neighboursOf(canton.option)
          .filter(n => hasCategoryRegionPage(category.slug, n.option))
          .map(n => ({ key: n.option, link: pageLink(category.slug, n.option), label: n.label }))}
        footerLink={{ link: categoryLink, label: `${category.label} in der ganzen Schweiz` }}
      />

      <CardGrid
        id="other-cats-title"
        heading={`Weitere Kategorien ${inCanton}`}
        alt
        items={categoriesWithPage(canton.option)
          .filter(cat => cat.slug !== category.slug)
          .map(cat => ({
            key: cat.slug,
            link: pageLink(cat.slug, canton.option),
            label: cat.label,
            image: cat.image,
          }))}
        footerLink={{
          link: { name: 'RegionLandingPage', params: { slug: params.region } },
          label: `Alle Anbieter:innen ${inCanton}`,
        }}
      />

      <TrustBanner />
      <Testimonials />

      <FinalCta
        heading={copy.finalCta?.heading || heading}
        text={`Vergleicht Angebote von Anbieter:innen, die ${inCanton} arbeiten, und bucht direkt über die Plattform.`}
        link={searchLink}
        label="Jetzt Angebote entdecken"
      />
    </LandingLayout>
  );
};

CategoryRegionLandingPageComponent.propTypes = { params: object };

const mapStateToProps = (state, ownProps) => {
  const { slug, region } = ownProps?.params || {};
  const { listingIds } = selectListingCollection(state, categoryRegionCollectionId(slug, region));
  return {
    listings: getListingsById(state, listingIds),
    scrollingDisabled: isScrollingDisabled(state),
  };
};

export default compose(connect(mapStateToProps))(CategoryRegionLandingPageComponent);
